// hooks/useSaldo.ts
import { useMemo } from 'react';
import { ITransacao as Transacao } from '../types/firestore.types';
import { Timestamp } from 'firebase/firestore';

export const useSaldo = (transacoes: Transacao[]) => {
    return useMemo(() => {

        let saldoDisponivel = 0;
        let saldoValidando = 0;

        // Diagnóstico: contagem por categoria, para saber o que entrou na conta
        // e o que foi ignorado por status/campo faltando.
        const detalhe = {
            totalTransacoes: transacoes?.length ?? 0,
            creditosContados: 0,
            creditosAguardando48h: 0,
            debitosContados: 0,
            debitosPendentes: 0,
            ignoradas: 0,
            saldoDisponivelBruto: 0,
            saldoValidandoBruto: 0,
        };

        if (!transacoes || transacoes.length === 0) {
            return {
                saldoDisponivel,
                saldoValidando,
                saldoTotal: saldoDisponivel + saldoValidando,
                detalhe
            };
        }

        transacoes.forEach((transacao) => {
            // Acumula em centavos inteiros. Somar reais como float deixa
            // resíduo (750 - 500 - 132.05 = 117.94999999999999), e o portão
            // do débito compara com `<`, então o cliente ficava impedido de
            // gastar o próprio saldo ao pagar o valor exato.
            const valor = Math.round((transacao.valor || 0) * 100);
            const tipoOperacao = transacao.tipo_operacao;
            const status = transacao.status;

            // Pular transações com status de falha
            if (status === 'failed' || status === 'cancelled' || status === 'expired') {
                detalhe.ignoradas++;
                return;
            }

            // Verificar se passaram 48 horas (apenas para créditos)
            const isMaisDe48Horas = (dataTransacao: Date): boolean => {
                const agora = new Date();
                const diferencaMs = agora.getTime() - dataTransacao.getTime();
                const diferencaHoras = diferencaMs / (1000 * 60 * 60);
                return diferencaHoras >= 48;
            };

            let dataTransacao: Date | null = null;

            // Tentar obter a data da transação (suporte para múltiplos campos)
            if (transacao.criado_em) {
                dataTransacao = transacao.criado_em instanceof Timestamp
                    ? transacao.criado_em.toDate()
                    : new Date(transacao.criado_em);
            } else if (transacao.createdAt) {
                dataTransacao = transacao.createdAt instanceof Timestamp
                    ? transacao.createdAt.toDate()
                    : new Date(transacao.createdAt);
            } else if (transacao.data_hora) {
                dataTransacao = transacao.data_hora instanceof Timestamp
                    ? transacao.data_hora.toDate()
                    : new Date(transacao.data_hora);
            }

            const passou48Horas = dataTransacao ? isMaisDe48Horas(dataTransacao) : false;

            // Lógica para créditos (recargas)
            if (tipoOperacao === 'credito') {
                if ((status === 'completed' || status === 'confirmed') && passou48Horas) {
                    saldoDisponivel += valor;
                    detalhe.creditosContados++;
                } else if (status === 'completed' || status === 'confirmed' || status === 'validating' || status === 'pendente_confirmacao') {
                    saldoValidando += valor;
                    detalhe.creditosAguardando48h++;
                } else {
                    // Crédito com status fora das listas acima (ex.: 'pending')
                    // não entra em nenhum dos dois saldos.
                    detalhe.ignoradas++;
                }
            }

            // Lógica para débitos (pagamentos)
            else if (tipoOperacao === 'debito') {
                if (status === 'completed' || status === 'confirmed') {
                    saldoDisponivel -= valor;
                    detalhe.debitosContados++;
                } else if (status === 'validating' || status === 'pendente_confirmacao') {
                    saldoValidando -= valor;
                    detalhe.debitosPendentes++;
                } else {
                    detalhe.ignoradas++;
                }
            }

            else {
                detalhe.ignoradas++;
            }
        });

        // Guarda os valores antes do clamp, em reais. Diferente de zero
        // negativo aqui significa que os débitos superaram os créditos
        // maduros — o clamp abaixo esconde isso.
        detalhe.saldoDisponivelBruto = saldoDisponivel / 100;
        detalhe.saldoValidandoBruto = saldoValidando / 100;

        // Garantir que os saldos não fiquem negativos
        saldoDisponivel = Math.max(0, saldoDisponivel);
        saldoValidando = Math.max(0, saldoValidando);

        // Soma ainda em centavos e converte uma única vez, para não
        // reintroduzir erro de float na própria soma.
        const saldoTotal = (saldoDisponivel + saldoValidando) / 100;

        return {
            saldoDisponivel: saldoDisponivel / 100,
            saldoValidando: saldoValidando / 100,
            saldoTotal,
            detalhe
        };
    }, [transacoes]);
};